import type { IResource } from '../../interface/src/index.ts'
import { getCurrentJob } from '../../resource/src/index.ts'

export const CANDIDATE_STATUS = {
  0: {
    id: 0,
    status: 'modules.recruitment.candidateStatus.ongoing',
    color: 'warning',
  },
  1: {
    id: 1,
    status: 'modules.recruitment.candidateStatus.rejectedAfterOffer',
    color: 'success',
  },
  2: {
    id: 2,
    status: 'modules.recruitment.candidateStatus.rejectedBeforeOffer',
    color: 'error',
  },
  3: {
    id: 3,
    status: 'modules.recruitment.candidateStatus.notHired',
    color: 'error',
  },
  4: {
    id: 4,
    status: 'modules.recruitment.candidateStatus.offerMade',
    color: 'success',
  },
  5: {
    id: 5,
    status: 'modules.recruitment.candidateStatus.offerDeclined',
    color: 'error',
  },
  6: {
    id: 6,
    status: 'modules.recruitment.candidateStatus.offerAccepted',
    color: 'success',
  },
  7: {
    id: 7,
    status: 'modules.recruitment.candidateStatus.recruited',
    color: 'success',
  },
}

export const currentProcessStatus = {
  TO_BE_DECIDED: 'TO_BE_DECIDED',
  TO_BE_PLANNED: 'TO_BE_PLANNED',
  PLANNED: 'PLANNED',
  TO_BE_EVALUATED: 'TO_BE_EVALUATED',
  OFFERED: 'OFFERED',
  OFFER_ACCEPTED: 'OFFER_ACCEPTED',
  OFFER_REJECTED: 'OFFER_REJECTED',
}

export const getLastResourceWantToMeet = (process: { logs: [{ actionId: 12 | 11; resourceId: string; resource: IResource }] }, actionId: number): IResource | null => {
  const wantToMeet = process.logs.filter(log => log.actionId === actionId)
  return wantToMeet[wantToMeet.length - 1]?.resource
}

export const setRecruitmentTemplate = (thisContent: string, process: any) => {
  let result = thisContent.replace('#firstname', process.candidateId.firstname).replace('#name', process.candidateId.lastname)

  let interviewDate: Date | null = null
  let recruiter1: IResource | null = null
  let recruiter2: IResource | null = null
  let roundNumber: number | null = null

  process.rounds.forEach((round: any, index: any) => {
    if (round.goToNext === null && interviewDate === null) {
      round.interviews.forEach((interview: any) => {
        const interviewStart = new Date(interview.meeting.events[0].start)
        // if (!RecruitmentModuleService.isInterviewCompleted(interview)) {
        if (!isInterviewCompleted(interview)) {
          interviewDate = interviewStart
          roundNumber = index + 1
          if (interview.recruiters && interview.recruiters.length > 0) {
            recruiter1 = interview.recruiters[0].recruiterId as IResource
            if (interview.recruiters.length > 1)
              recruiter2 = interview.recruiters[1].recruiterId
          }
        }
      })
    }
  })
  if (interviewDate !== null) {
    result = result.replace('#interview-date', dateFormat(interviewDate))
    result = result.replace('#interview-time', timeFormat(interviewDate))
    if (roundNumber !== null)
      result = result.replace('#round-number', `${roundNumber}`)

    if (recruiter1 !== null) {
      result = result.replace('#recruiter-name-1', `${(recruiter1 as IResource).firstname}  ${(recruiter1 as IResource).lastname}`)

      const job = getCurrentJob(recruiter1)
      if (!job || typeof job.positionId === 'string')
        throw new Error('positionId is not a IResource')
      if (job)
        result = result.replace('#recruiter-job-1', job.positionId.display)
    }
    if (recruiter2 !== null) {
      result = result.replace('#recruiter-name-2', `${(recruiter2 as IResource).firstname}  ${(recruiter2 as IResource).lastname}`)

      const job = getCurrentJob(recruiter2)
      if (!job || typeof job.positionId === 'string')
        throw new Error('positionId is not a IResource')
      if (job)
        result = result.replace('#recruiter-job-2', job.positionId.display)
    }
  }

  return result
}

export const isInterviewCompleted = (interview: { recruiters: Array<{ evaluationCompleted: boolean; noEvaluation: boolean }>; interviewDate: string | Date }) => {
  if (interview.recruiters.length == 0)
    return false
  let count = 0
  for (let i = 0; i < interview.recruiters.length; i++) {
    if (interview.recruiters[i].evaluationCompleted || interview.recruiters[i].noEvaluation === true) {
      count++
    }
  }
  const isAllNoEvaluation = interview.recruiters.every(recruiter => recruiter.noEvaluation)
  return (count == interview.recruiters.length) && (!isAllNoEvaluation || new Date() > new Date(interview.interviewDate))
}

export const dateFormat = (stringDate: string | Date, user = undefined, lang = undefined): string => {
  if (!stringDate || stringDate == '-')
    return '-'

  const thisDate = new Date(stringDate)

  return thisDate.toLocaleDateString(lang, { year: 'numeric', month: 'long', day: '2-digit' })
}

export const timeFormat = (stringDate: string | Date) => {
  const thisDate = new Date(stringDate)
  const curr_hour = thisDate.getHours() < 10 ? `0${thisDate.getHours()}` : thisDate.getHours()
  const curr_minutes = thisDate.getMinutes() < 10 ? `0${thisDate.getMinutes()}` : thisDate.getMinutes()

  return `${curr_hour}:${curr_minutes}`
}

type offerAccepted = { rounds: Array<{ proposalAcceptedDate: string | Date | undefined | null }> }
export const isOfferAccepted = (process: offerAccepted) => {
  if (!process)
    return false
  return process.rounds.find(round => round.proposalAcceptedDate)
}

type proposalMade = { rounds: Array<{ proposalDate: string | Date | undefined | null }> }
export const isProposalMade = (process: proposalMade) => {
  if (!process)
    return false
  return process.rounds.find(round => round.proposalDate)
}

type proposalRejected = { rounds: Array<{ noGo: boolean }>; isRejectedUpfront: boolean }
export const isRejected = (process: proposalRejected) => {
  if (!process)
    return false
  if (process.isRejectedUpfront)
    return true
  return process.rounds.find(round => round.noGo)
}

type candidateCancelled = { rounds: Array<{ candidateCancelled: boolean }>; isActive: boolean }
export const hasCandidateCancelled = (process: candidateCancelled) => {
  if (!process || process.isActive)
    return false
  return process.rounds.find(round => round.candidateCancelled)
}

type hiredProcess = { hiredDate: string | Date | null }
export const isCandidateHired = (process: hiredProcess) => {
  return !!process.hiredDate
}

type offerRejected = { rounds: Array<{ proposalRejectedDate: string | Date | undefined | null }> }
export const isOfferRejected = (process: offerRejected) => {
  if (!process)
    return false
  return process.rounds.find(round => round.proposalRejectedDate)
}

export const getProcessStatusContent = (process: { currentStatus: number }) => {
  return CANDIDATE_STATUS[process.currentStatus as keyof typeof CANDIDATE_STATUS] || {
    status: 'modules.recruitment.candidateStatus.notRecongnized',
    color: 'disabled',
  }
}

export const countMeetingNull = (round: undefined | { interviews: { meeting: {} }[] }) => {
  let count = 0
  if (round) {
    for (let i = 0; i < round.interviews.length; i++) {
      if (round.interviews[i].meeting === null)
        count++
    }
  }
  return count
}

type TRoundCompletion = undefined | {
  evaluationCompleted: any;
  oneEvaluation: any;
  interviews: { recruiters: { isCoEvaluator: boolean; noEvaluation: boolean; evaluationCompleted: boolean }[]; interviewDate: string | Date }[];
}
export const getRoundCompletion = (round: TRoundCompletion) => {
  if (!round)
    return 0

  let total = 0
  let count = 0

  if (round.oneEvaluation)
    return round.evaluationCompleted ? 100 : 0

  for (let j = 0; j < round.interviews.length; j++) {
    if (round.interviews[j].recruiters.length == 0)
      total++

    for (let i = 0; i < round.interviews[j].recruiters.length; i++) {
      total++
      if (round.interviews[j].recruiters[i].evaluationCompleted
        || round.interviews[j].recruiters[i].noEvaluation === true
        || (round.interviews[j].recruiters[i].isCoEvaluator === true && new Date(round.interviews[j].interviewDate) < new Date()))
        count++
    };
  };
  if (total == 0)
    return 0

  return Math.floor(count / total * 100)
}

export const getRecruiterCoverage = (round: { interviews: { recruiters: {}[] }[] }) => {
  let count = 0
  for (let i = 0; i < round.interviews.length; i++) {
    if (round.interviews[i].recruiters.length > 0)
      count++
  }
  ;
  return count
}

export const getDueDate = (process: { _created: string | Date; rounds: { interviews: { interviewDate?: string | Date; meeting: null | { events: { start: string | Date }[] } }[] }[] }) => {
  const dateNow = new Date().getTime()
  const MS_PER_DAY = 1000 * 60 * 60 * 24

  // Find the last round with interviews
  const tempRounds = process.rounds?.filter(round => round.interviews.length > 0) || []
  const round = tempRounds[tempRounds.length - 1]

  // If no rounds with interviews, use process creation date
  if (!round || !round.interviews || round.interviews.length === 0) {
    const createdDate = new Date(process._created).getTime()
    if (isNaN(createdDate)) {
      return 0
    }
    if (createdDate < dateNow) {
      return ((dateNow - createdDate) / MS_PER_DAY) * -1
    }
    return (createdDate - dateNow) / MS_PER_DAY
  }

  // Collect all meeting dates from interviews
  const meetingDates: number[] = []
  for (const interview of round.interviews) {
    if (interview.meeting === null) {
      return 0 // Meeting not scheduled yet
    }

    // Try to get the date from the meeting events, or fall back to interviewDate
    let interviewTime: number | null = null

    // Check if meeting is a populated object with events array
    if (
      interview.meeting &&
      typeof interview.meeting === 'object' &&
      'events' in interview.meeting &&
      Array.isArray(interview.meeting.events) &&
      interview.meeting.events.length > 0 &&
      interview.meeting.events[0]?.start
    ) {
      const startValue = interview.meeting.events[0].start
      // Handle both string dates and objects with custom date format (Vue devtools format)
      const dateValue = typeof startValue === 'object' && startValue !== null && '_custom' in (startValue as any)
        ? (startValue as any)._custom?.value || (startValue as any)._custom?.displayText
        : startValue
      interviewTime = new Date(dateValue).getTime()
    }

    // Fall back to interviewDate if meeting date is invalid or not available
    if (!interviewTime || isNaN(interviewTime)) {
      if (interview.interviewDate) {
        interviewTime = new Date(interview.interviewDate).getTime()
      }
    }

    // Only add valid dates
    if (interviewTime && !isNaN(interviewTime)) {
      meetingDates.push(interviewTime)
    }
  }

  // If no valid meeting dates found, return 0
  if (meetingDates.length === 0) {
    return 0
  }

  const minDate = Math.min(...meetingDates)
  const maxDate = Math.max(...meetingDates)

  // All meetings are in the past - return negative number (days since last meeting)
  if (maxDate < dateNow) {
    const res = (dateNow - maxDate) / MS_PER_DAY
    return res === 0 ? 0 : -res
  }

  // All meetings are in the future
  if (minDate > dateNow) {
    return (minDate - dateNow) / MS_PER_DAY
  }

  // Some meetings are past, some are future
  return (maxDate - dateNow) / MS_PER_DAY
}

export const getProcessStatus = (process: any) => {
  if (process.rounds.length === 0)
    return currentProcessStatus.TO_BE_DECIDED

  if (process.rounds.length === 1 && process.rounds[0].interviews.length === 0 && process.screeningStatus === 3)
    return currentProcessStatus.TO_BE_PLANNED

  const lastRoundWithInterview = process.rounds.reduce((a: any, b: any) => {
    if (b.interviews.length === 0 && (!a || a.interviews.length === 0))
      return
    return b.interviews.length === 0 || new Date(a._created) > new Date(b._created) ? a : b
  })
  const round = lastRoundWithInterview || process.rounds[process.rounds.length - 1]
  const roundCompletion = getRoundCompletion(round)
  const ancientBool = (round && round.dueDate <= 0 && (round.goToNext !== null || countMeetingNull(round) > 0) && round.proposalDate === null && (roundCompletion == 100 || countMeetingNull(round) > 0))
  const newBool = (round && round.proposalDate === null && (round.interviews.length === 0 || round.goToNext))

  if (isProposalMade(process) && !isOfferAccepted(process) && !isOfferRejected(process))
    return currentProcessStatus.OFFERED
  else if (isOfferAccepted(process))
    return currentProcessStatus.OFFER_ACCEPTED
  else if (isOfferRejected(process))
    return currentProcessStatus.OFFER_REJECTED
  else if (countMeetingNull(round) === 0 && round && round.dueDate >= 0 && round.goToNext === null && round.proposalDate === null && (round.dueDate > 0 || roundCompletion !== 100 || getRecruiterCoverage(round) === 0))
    return currentProcessStatus.PLANNED
  else if (!round || (round && round.interviews.length > 0 && getDueDate(process) < 0 && round.goToNext === null && round.proposalDate === null && roundCompletion != 100))
    return currentProcessStatus.TO_BE_EVALUATED
  else if (newBool || ancientBool)
    return currentProcessStatus.TO_BE_PLANNED
  else //  (round && round.goToNext === null && round.proposalDate === null && roundCompletion === 100)
    return currentProcessStatus.TO_BE_DECIDED
}

export const isRecruitmentInProgress = (process: any) => {
  return !hasCandidateCancelled(process) && !isRejected(process) && !isOfferRejected(process) && !isProposalMade(process) && !isOfferAccepted(process)
}

export const getCandidateStatus = (process: any) => {

  if (isRecruitmentInProgress(process)) return 0
  if (hasCandidateCancelled(process) && (isProposalMade(process) || isOfferAccepted(process))) return 1
  if (hasCandidateCancelled(process) && !isProposalMade(process)) return 2
  if (isRejected(process)) return 3
  if (isProposalMade(process) && !hasCandidateCancelled(process) && !isOfferRejected(process) && !isOfferAccepted(process)) return 4
  if (isOfferRejected(process)) return 5
  if (isCandidateHired(process)) return 7
  if (isOfferAccepted(process) && !hasCandidateCancelled(process)) return 6

  return 0
}

export const getProcessStep = (process: any) => {
  if (process.isActive) {
    let count = 0

    for (let j = 0; j < process.rounds.length; j++)
      count += process.rounds[j].interviews.length

    if (count === 0)
      return 0

    if (isProposalMade(process) && !isOfferAccepted(process) && !isOfferRejected(process))
      return 4
    else if (isOfferAccepted(process))
      return 5
    else if (isOfferRejected(process))
      return 6

    for (let i = process.rounds.length - 1; i >= 0; i--) {
      if (process.rounds[i].goToNext !== null)
        return i + 2 < 4 ? i + 2 : 3

      if (i === 0)
        return 1
    }
  }
}

const isOpenRound = (round: { goToNext?: Date | null }) => round?.goToNext == null

export const getActiveRound = ({ rounds }: { rounds: any }) => {
  const index = rounds.findIndex((round: any) => isOpenRound(round))
  return index === -1 ? rounds[rounds.length - 1] : rounds[index]
}

export const getActiveRoundIndex = (process: any) => {
  const rounds = process?.rounds ?? []
  if (rounds.length === 0) return 0
  const index = rounds.findIndex((round: any) => isOpenRound(round))
  // goToNext closes the round. If every saved round is closed, the candidate is
  // already in the next one, even when that round has no interview yet.
  if (index === -1) return rounds.length
  return index
}

export const sources = [
  { _id: 0, display: 'components.recruitment.candidateSource.spontaneousApplicationByMail' },
  { _id: 1, display: 'components.recruitment.candidateSource.spontaneousApplicationThroughWebsite' },
  { _id: 2, display: 'components.recruitment.candidateSource.recruitmentFirm' },
  { _id: 3, display: 'components.recruitment.candidateSource.employeeReferral' },
  { _id: 7, display: 'components.recruitment.candidateSource.internalHeadhunting' },
  { _id: 4, display: 'components.recruitment.candidateSource.alumniOrClientReferral' },
  { _id: 5, display: 'components.recruitment.candidateSource.schoolForum' },
  { _id: 6, display: 'components.recruitment.candidateSource.recruitmentEvent' },
  { _id: 8, display: 'components.recruitment.candidateSource.jobBoard' },
  { _id: 99, display: 'components.recruitment.candidateSource.yourWebsite' },
]

// type TOpenRoundProcess = { rounds: TRoundCompletion[] }
// export const getOpenRound = (process: TOpenRoundProcess) => {
//   for (let i = 0; i < process.rounds.length; i++) {
//     const round = process.rounds[i]
//     if (!round || round.interviews.length === 0 || getRoundCompletion(round) < 100)
//       return i + 1

//     if (getRoundCompletion(round) === 100 && process.rounds[i + 1] === undefined)

type TOpenRoundProcess = { rounds: TRoundCompletion[] } | { rounds: { interviews: any[] }[] }
export const getOpenRound = (process: TOpenRoundProcess) => {
  for (let i = 0; i < process.rounds.length; i++) {
    const round = process.rounds[i] as any
    const hasCompletionFields = round && (('oneEvaluation' in round) || ('evaluationCompleted' in round))
    const completion = hasCompletionFields ? getRoundCompletion(round as TRoundCompletion) : 0

    if (!round || round.interviews.length === 0 || completion < 100)
      return i + 1

    if (completion === 100 && process.rounds[i + 1] === undefined)



      return i + 2
  };
  return 1
}

type TIsMeetingInProcessProcess = { rounds: { interviews: { meeting: { _id: string } }[] }[] }
const isMeetingInProcess = (process: TIsMeetingInProcessProcess, meeting?: { _id: string } | null) => {
  if (!meeting?._id)
    return null
  return process.rounds.some((round) => {
    return round.interviews.some(interview => interview.meeting?._id === meeting._id)
  })
}

type TCreateMeetingSubjectProcess = {
  // position: any
  // candidate: any candidateId: { firstname: string; lastname: string }; positionId: { acronym: string }; rounds: { interviews: { meeting: { _id: string } }[] }[] 
  position: any;
  candidate: any;
  candidateId: { firstname: string; lastname: string };
  positionId: { acronym: string };
  rounds: { interviews: { meeting: { _id: string } }[] }[];
}
type TCreateMeetingSubjectMeeting = { _id: string; moduleId: number; isMultiple: boolean; events: { summary: string; location: string; attendees: { attendeeId: string; name: string }[] }[] }
type TCreateMeetingInterview = { recruiters: { recruiter: { firstname: string; lastname: string }; recruiterId: string }[] }
export const createMeetingSubject = (process: TCreateMeetingSubjectProcess, meeting: TCreateMeetingSubjectMeeting, interview: TCreateMeetingInterview, targetRoundIndex?: number) => {
  if (meeting.isMultiple === true)
    return meeting.events[0].summary
  const meetingLocation = meeting?.events?.[0]?.location || 'TBD'

  let recruiterNames = ''
  if (process && process !== null) {
    // const thisInterview = !meeting._id
    //   ? null
    //   : process.rounds.reduce((acc, round) => {
    //     const foundInterview = round.interviews.find((interview: any) => interview.meeting?._id === meeting._id)
    //     return foundInterview || acc
    //   }, interview)
    const thisInterview: TCreateMeetingInterview | null = !meeting._id
      ? null
      : process.rounds.reduce<TCreateMeetingInterview | null>((acc, round) => {
        const foundInterview = round.interviews.find((interview: any) => interview.meeting?._id === meeting._id)
        return (foundInterview as unknown as TCreateMeetingInterview) || acc
      }, null) || interview    

    if (thisInterview !== null) {
      recruiterNames = thisInterview.recruiters.reduce((acc: string, recruiter: any) => {
        return `${acc} - ${recruiter.recruiter.firstname} ${recruiter.recruiter.lastname}`
      }, recruiterNames)
    }
    else {
      if (meeting.events[0].attendees) {
        recruiterNames = meeting.events[0].attendees.reduce((acc: string, attendee: any) => {
          if (attendee.attendeeId !== null)
            return `${acc} - ${attendee.name}`
          return acc
        }, recruiterNames)
      };
    }
  }
  else {
    if (meeting.events[0].attendees) {
      recruiterNames = meeting.events[0].attendees.reduce((acc: string, attendee: any) => {
        if (attendee.attendeeId !== null)
          return `${acc} - ${attendee.name}`
        return acc
      }, recruiterNames)
    };
  };

  if (process) {
    const openRound = targetRoundIndex != null ? targetRoundIndex + 1 : getOpenRound(process)
    const openRoundIndex = openRound - 1
    const meetingInProcess = isMeetingInProcess(process, meeting)
    let interviewNumber = 1
    if (process.rounds[openRoundIndex] !== undefined) {
      // if (this.isMeetingInProcess(process, meeting)===false) {
      if (meetingInProcess) {
        process.rounds.forEach((round) => {
          round.interviews.forEach((interview, index) => {
            if (interview.meeting !== null && interview.meeting._id === meeting._id)
              interviewNumber = index + 1
          })
        })

        if (!interviewNumber)
          interviewNumber = process.rounds[0].interviews.length + 1
      }
      else if (meetingInProcess === null) {
        interviewNumber = (process.rounds[openRoundIndex].interviews.length)
      }
      else {
        interviewNumber = (process.rounds[openRoundIndex].interviews.length + 1)
      }
      if (interviewNumber === 0)
        interviewNumber = 1
    }
    const roundLabel = `Step ${openRound}.${interviewNumber}`
    return `${process.candidate.firstname} ${process.candidate.lastname} - ${process.position.acronym} - ${roundLabel}${recruiterNames} - ${meetingLocation}`
  }
  else {
    let moduleInfo = 'MR'
    if (meeting.moduleId === 4)
      moduleInfo = 'Recruitment'

    else if (meeting.moduleId === 9)
      moduleInfo = 'Training'

    return `${moduleInfo + recruiterNames} - ${meetingLocation}`
  }
}

export const getMeetingTemplate = (firmId: string) => {
  return {
    method: 'REQUEST',
    _firmId: firmId,
    sentDate: [],
    events: [
      {
        duration: 60,
        start: null,
        end: null,
        transp: 'OPAQUE',
        summary: 'Recruitment - TBD',
        alarms: [15],
        uid: null,
        sequence: 0,
        stamp: new Date(),
        floating: false,
        location: 'TBD',
        description: null,
        organizer: {
          name: 'BestConsultant',
          // email: '04ffc90898c6319f6eafc79a72ed26ad@inbound.postmarkapp.com'
          email: '0bd777a454b84f5a7c7f46b40fa5464e@inbound.postmarkapp.com',
        },
        attendees: [],
        method: 'PUBLISH',
        status: 'CONFIRMED',
        interviewDate: undefined,
      },
    ],
  }
}

type TMeetingCheckFuture = { events: { start: string | Date }[] } | null
export const isInterviewFuture = (meeting: TMeetingCheckFuture): boolean => {
  if (meeting === null)
    return true

  return new Date() < meeting.events[0].start
}

type TGetAttendeeStatus = { meeting: { events: { attendees: { attendeeId: string | { _id: string } }[] }[] }; userId: string }
export const getAttendeeStatus = ({ meeting, userId }: TGetAttendeeStatus) => {
  if (meeting === null || !meeting.events[0].attendees || !userId)
    return null

  return meeting.events[0].attendees.find((attendee) => {
    if (!attendee.attendeeId)
      return false
    const attendeeId = typeof attendee.attendeeId === 'string' ? attendee.attendeeId : attendee.attendeeId._id
    return attendeeId === userId
  }) || null
}

// export const isInterviewCompleted = (interview, round) => {
//   if (round.oneEvaluation && round.evaluationCompleted !== null)
//     return true

//   return RecruitmentModuleService.isInterviewCompleted(interview)
// }

export const hasMeetingChanged = (meeting: any, oldMeeting: any) => {
  const isDateChanged = new Date(oldMeeting.events[0].start).getTime() !== new Date(meeting.events[0].start).getTime()
  const isDurationChanged = meeting.events[0].duration !== oldMeeting.events[0].duration
  const isNumberOfAttendeesChanged = meeting.events[0].attendees.length !== oldMeeting.events[0].attendees.length

  if (isDateChanged || isDurationChanged || isNumberOfAttendeesChanged)
    return true

  // const isAttendeeChanged = oldMeeting.events[0].attendees.some((attendee, index) => {
  //   return attendee.attendeeId?._id !== meeting.events[0].attendees[index].attendeeId?._id
    const isAttendeeChanged = oldMeeting.events[0].attendees.some((attendee: { attendeeId?: { _id?: string } | string | null }, index: number) => {
    const oldId = typeof attendee.attendeeId === 'string' ? attendee.attendeeId : attendee.attendeeId?._id
    const newAttendee = meeting.events[0].attendees[index]
    const newId = typeof newAttendee.attendeeId === 'string' ? newAttendee.attendeeId : newAttendee.attendeeId?._id
    return oldId !== newId
  })

  return isAttendeeChanged
}

export const hasMeetingTimeChanged = (meeting: any, oldMeeting: any) => {
  const hasTimeChanged = new Date(oldMeeting.events[0].start).getTime() !== new Date(meeting.events[0].start).getTime()
  const hasDurationChanged = meeting.events[0].duration !== oldMeeting.events[0].duration

  return hasTimeChanged || hasDurationChanged
}

type TRoundIsCompleted = { interviews: { meeting: { events: { start: Date }[] }; recruiters: { isCoEvaluator: boolean; noEvaluation: boolean; evaluationCompleted: boolean; evaluationForm?: unknown | null }[] }[]; oneEvaluation: boolean; evaluationCompleted: boolean | null | undefined }
export const isRoundCompleted = (round: TRoundIsCompleted) => {
  if (round.oneEvaluation)
    return round.evaluationCompleted != null

  const now = new Date()
  now.setHours(now.getHours() + 1) // adding one hour tolerance
  for (const interview of round.interviews) {
    if (interview.recruiters.length < 1)
      return false
    if (interview.meeting && !(interview.meeting.events[0].start < now))
      return false
    for (const recruiter of interview.recruiters) {
      if (recruiter.isCoEvaluator !== true && (recruiter.noEvaluation !== true && recruiter.evaluationForm !== null) && !recruiter.evaluationCompleted)
        return false
    }
  }

  return true
}


export const updateScreeningStatus = (application:{screeningStatus: number | null, nextContact: Date | null, isActive: boolean, logs: {resource: any, resourceId: string, actionId: number, _created: Date}[]}, value: number, author: {_id: string, firstname: string, lastname: string}) => {
  
  
  if (application.screeningStatus == value) {
    application.screeningStatus = null
    application.nextContact = null
  }
  else {
    application.screeningStatus = value
  }
  // const toSend = { screeningStatus: application.screeningStatus, logs: application.logs, nextContact:null }
  const toSend: {
    screeningStatus: number | null,
    logs: { resource: any, resourceId: string, actionId: number, _created: Date }[],
    nextContact: Date | null,
    isActive: boolean,
    rounds?: { interviews: any[] }[]
  } = { screeningStatus: application.screeningStatus, logs: application.logs, nextContact: application.nextContact as Date | null, isActive: application.isActive }

  if (application.screeningStatus !== null) {
    if (value === 2) {
      toSend.nextContact = application.nextContact
      toSend.isActive = !application.nextContact
    };
    if (value === 3)
      toSend.rounds = [{ interviews: [] }]

    else
      toSend.rounds = []

    if (!toSend.logs)
      toSend.logs = []

    if (value === 1){
      // toSend.nextContact = null;
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 11, _created: new Date() })
    }
      

    if (value === 3){
      // toSend.nextContact = null;
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 10, _created: new Date()})
    }
      
    if (value === 2)
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 12, _created: new Date() })

    if (value === 4)
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 16, _created: new Date() })
  }
  else {
    if (value === 1){
     
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 14, _created: new Date() })
      toSend.isActive = true
    }

    if (value === 3)
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 13, _created: new Date() })

    if (value === 2) {
      // toSend.nextContact = null
      toSend.isActive = true
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 15, _created: new Date() })
    }

    if (value === 4) {
      toSend.isActive = true
      toSend.logs.push({ resource: author, resourceId: author._id, actionId: 17, _created: new Date() })
    }
  }

  return {
    updatedValues: toSend,
  }
}


export {
  DEFAULT_SLOT_CREATION_HOURS,
  DEFAULT_SLOT_SNAP_MINUTES,
  SLOT_SNAP_MINUTE_OPTIONS,
  clampSlotStartTime,
  formatHourLabel,
  getAllowedSlotHourValues,
  getAllowedSlotMinuteValues,
  isSlotStartTimeAllowed,
  resolveSlotCreationHours,
  type TSlotCreationHours,
  type TSlotSnapMinutes,
} from './slotCreationHours.ts'

export const meetingDurations = [
  { _id: 30, display: '30min' },
  { _id: 45, display: '45min' },
  { _id: 50, display: '50min' },
  { _id: 60, display: '1h' },
  { _id: 75, display: '1h15' },
  { _id: 90, display: '1h30' },
  { _id: 120, display: '2h' },
  { _id: 150, display: '2h30' },
  { _id: 180, display: '3h' },
]

export {
  areRecruitmentProcessInterviewsClosed,
  canDeleteRecruitmentProcess,
  getRecruitmentProcessDeleteBlockReason,
  getRecruitmentProcessInterviews,
} from './deleteProcess.ts'

export * from './jobBoards.ts'
export * from './jobTeaser.ts'
